import React from 'react';
import { Check, Link } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getContrastColor } from '../../lib/colorContrast';

const renderFormattedText = (text, searchTerm, highlightText) => {
  if (!text || typeof text !== 'string') return null;

  const lines = text.split('\n');
  return lines.map((line, lineIndex) => {
    const lineKey = `line-${lineIndex}`;
    let content;

    if (line.startsWith('• ')) {
      content = (
        <div key={lineKey} className="flex">
          <span className="w-4 flex-shrink-0">•</span>
          <span>{searchTerm ? highlightText(line.substring(2), searchTerm) : line.substring(2)}</span>
        </div>
      );
    } else if (line.startsWith('    ')) {
      content = (
        <div key={lineKey} className="pl-4">
          {searchTerm ? highlightText(line.trim(), searchTerm) : line.trim()}
        </div>
      );
    } else {
      content = (
        <div key={lineKey}>
          {searchTerm ? highlightText(line, searchTerm) : line}
        </div>
      );
    }

    return content;
  });
};

const TopicItem = ({ topic, isSelected, onToggle, searchTerm, highlightText }) => {
  const highlightedConcept = topic.concepts?.find(concept => concept.is_highlighted);
  const otherConcepts = topic.concepts?.filter(concept => !concept.is_highlighted) || [];

  const getRelativeTime = (date) => {
    if (!date) return { text: 'Date unknown', color: 'bg-gray-500' };

    const distanceString = formatDistanceToNow(new Date(date), { addSuffix: true });
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));

    if (days < 7) return { text: distanceString, color: 'bg-green-500' };
    if (days < 30) return { text: distanceString, color: 'bg-blue-500' };
    if (days < 365) return { text: distanceString, color: 'bg-yellow-500' };
    return { text: distanceString, color: 'bg-red-500' };
  };

  const { text: formattedDate, color: dateColor } = getRelativeTime(topic.created_at);

  return (
    <div
      className={`px-4 py-3 border-2 border-neutral-900 rounded-lg cursor-pointer hover:bg-neutral-50 mb-2 ${
        isSelected ? 'bg-neutral-100 border-neutral-300' : ''
      } relative`}
      onClick={onToggle}
    >
      <div className="flex items-start justify-between">
        <div className="flex-grow mr-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-medium text-neutral-900">
              {highlightText(topic.name, searchTerm)}
            </h3>
            {topic.tag && (
              <span 
                className="inline-block rounded-full px-2 py-0.5 text-xs border border-neutral-200"
                style={{
                  backgroundColor: topic.tag.color || '#E6E6FF',
                  color: getContrastColor(topic.tag.color || '#E6E6FF')
                }}
              >
                {highlightText(topic.tag.name, searchTerm)}
              </span>
            )}
          </div>

          {/* Updated basket name styling */}
          {topic.basket && topic.basket.name && (
            <div className="flex items-center gap-1 mt-1">
              <Link size={12} className="text-blue-500" />
              <span className="text-xs text-blue-500 italic">
                {highlightText(topic.basket.name, searchTerm)}
              </span>
            </div>
          )}
          
          {/* Update highlighted concept styling */}
          {highlightedConcept && (
            <div className="mt-2 relative pl-4 py-1">
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cranberry-400"></div>
              <div className="text-xs text-neutral-600">
                {renderFormattedText(highlightedConcept.text, searchTerm, highlightText)}
              </div>
            </div>
          )}
          
          {/* Other concepts remain the same */}
          {otherConcepts.length > 0 && (
            <ul className="mt-2 space-y-1">
              {otherConcepts.map((concept, index) => (
                <li key={index} className="text-xs text-gray-600 flex">
                  <span className="w-4 flex-shrink-0">•</span>
                  <span>{renderFormattedText(concept.text, searchTerm, highlightText)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col items-end justify-between h-full">
          <span className={`text-xs text-white mb-1 px-2 py-1 rounded-lg ${dateColor}`}>
            {formattedDate}
          </span>
          {/* Updated check icon positioning and styling */}
          <div className="flex-grow flex items-center">
            {isSelected && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Check className="text-emerald-600 flex-shrink-0 stroke-[3]" size={24} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicItem;
